import { Dispatch, FormEvent, useState } from 'react';
import CnpjTextField from '@/components/NumberFormat/cnpjTextField';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { handleErrorAsync } from '@/utils/handlers';
import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  NativeSelect,
  TextField,
  Typography,
} from '@mui/material';
import { ICompanyData } from './View';

interface ISelectCompanyProps {
  onAdvancedStep: Dispatch<ICompanyData>;
  isEditingCompany: boolean;
}

const SelectCompany = ({
  onAdvancedStep,
  isEditingCompany,
}: ISelectCompanyProps) => {
  const { setIsLoading } = useLoading();
  const { user, defaultMembership } = useUser();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [newCnpj, setNewCnpj] = useState(defaultMembership?.company.cnpj || '');
  const [cnpjError, setCnpjError] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const cnpj = defaultMembership ? formData.get('cnpj') : newCnpj;
    const isDefaultRefusedMembership = !!user.memberships?.find(
      membership =>
        membership.is_default &&
        membership.is_refused &&
        membership.company.cnpj ===
          cnpj?.toString()?.replace(/[./-]/g, '').toUpperCase()
    );
    if (!isDefaultRefusedMembership) {
      const [result, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        const { data } = await api.post<ICompanyData>(
          '/api/companies/lookup/',
          {
            cnpj,
          }
        );

        return {
          companyData: data,
        };
      }).finally(() => setIsLoading(false));

      if (error) {
        Toast('error', error.message);
        setCnpjError(true);
        return;
      }

      onAdvancedStep(result.companyData);
    } else {
      Toast(
        'error',
        'Você já foi recusado nessa empresa. Por favor, informe outro CNPJ.'
      );
    }
  };

  return (
    <>
      <Box
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: mdUp ? '380px' : '100%',
          margin: 'auto',
        }}
        data-testid="form-inform-cnpj"
        component="form"
      >
        <Typography
          variant="h6"
          sx={{
            mb: '16px',
            textAlign: 'left',
            marginTop: 'calc(28vh - 56px)',
            color: 'text.secondary',
          }}
        >
          {!defaultMembership
            ? 'Informe o CNPJ'
            : 'Escolha a empresa que você quer configurar!'}
        </Typography>
        <FormControl fullWidth>
          {!defaultMembership ? (
            <TextField
              variant="standard"
              placeholder="00.000.000/0001-00"
              name="cnpj"
              label="CNPJ"
              id="cnpj"
              value={newCnpj}
              onChange={e => {
                setCnpjError(false);
                setNewCnpj(e.target.value);
              }}
              error={cnpjError}
              InputProps={{
                inputComponent: CnpjTextField as never,
              }}
              sx={{ mb: 2, width: '100%' }}
            />
          ) : (
            <>
              <InputLabel variant="standard" htmlFor="uncontrolled-native">
                Empresas
              </InputLabel>
              <NativeSelect
                inputProps={{
                  name: 'cnpj',
                  id: 'cnpj',
                  placeholder: 'Sua empresa',
                }}
              >
                {user?.memberships?.map(membership => {
                  const canEditCompany =
                    isEditingCompany &&
                    membership.company.has_confirmed_configuration &&
                    !membership.is_refused &&
                    membership.is_validated &&
                    (membership.is_superadmin ||
                      membership.has_confirmed_configuration);

                  const canConfigCompany =
                    !isEditingCompany &&
                    !membership.company.has_confirmed_configuration &&
                    !membership.is_refused;

                  if (canEditCompany || canConfigCompany) {
                    return (
                      <option
                        key={membership.id}
                        value={membership.company.cnpj}
                      >
                        {membership.company.short_name ||
                          membership.company.full_name}
                      </option>
                    );
                  }
                })}
              </NativeSelect>
            </>
          )}
        </FormControl>
        <Button
          variant="contained"
          type="submit"
          className="submitButton"
          data-testid="button-next-step"
        >
          Próximo
        </Button>
      </Box>
    </>
  );
};

export type { ISelectCompanyProps };
export default SelectCompany;
